import React from "react"
import styled from "styled-components"
import PaddedContainer from "../ui/PaddedContainer"
import CloseIcon from "@mui/icons-material/Close"
import CheckIcon from "@mui/icons-material/Check"
import QuestionIcon from "@mui/icons-material/QuestionMark"
import {
  SensaiComponentZonesTableZone,
  Sensaienum_Componentindividualtablecell_Content,
} from "../../gql/generated/graphql"
import { Box, useMediaQuery, useTheme } from "@mui/system"

import { GatsbyImage } from "gatsby-plugin-image"
import { Typography } from "@mui/material"

const Grid = styled.div<{ cols: number; rows?: number; isMobile?: boolean }>`
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols}, 1fr)`};
  column-gap: ${({ isMobile }) => (isMobile ? "0" : "10px")};
  overflow-x: scroll;
`
//width: ${({ isMobile }) => (isMobile ? "100%" : "75%")};

const ColumnHeadingBox = styled.div<{ highlighted?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border: ${({ highlighted }) => (highlighted ? "3px solid #1d99b8" : "none")};
  border-radius: ${({ highlighted }) => (highlighted ? "5px" : "none")};
  margin-bottom: ${({ highlighted }) => (highlighted ? "5px" : 0)};
  margin-top: ${({ highlighted }) => (highlighted ? "5px" : 0)};
  padding: 10px;
  min-height: 60px;
  background: #ffffff;
`

const ColumnHeading = styled.span`
  line-height: 1.2rem;
  text-align: center;
  font-family: "Rubik";
  font-size: 16px;
  font-weight: 600;
  color: #000;
`

const Cell = styled.div<{
  highlighted?: boolean
  rows?: number
  idx?: number
  isMobile?: boolean
  colIdx?: number
}>`
  border: ${({ highlighted }) => (highlighted ? "3px solid #1d99b8" : "none")};
  border-bottom: ${({ highlighted }) =>
    highlighted ? "3px solid #1d99b8" : "1px solid #CAC6C6"};
  border-left: ${({ highlighted, isMobile, colIdx }) =>
    !highlighted && isMobile && colIdx !== 1 && "1px solid #CAC6C6"};

  border-top: ${({ highlighted, isMobile, idx }) =>
    highlighted && isMobile && idx !== 0 && "none"};
  display: flex;
  justify-content: center;
  align-items: center;
  color: #454545;
  padding: 15px;
  flex-direction: column;
  line-height: 1.3rem;
  min-width: 70px;
  margin-bottom: ${({ highlighted, isMobile, rows, idx }) =>
    highlighted && !isMobile && rows && idx !== rows - 1 ? "5px" : 0};
  background-color: ${({ isMobile }) =>
    !isMobile ? "rgb(243, 246, 248)" : ""};
`

const RowHeader = styled(Cell)<{ isMobile?: boolean; numColumns: number }>`
  font-family: "Rubik";
  justify-content: ${({ isMobile }) => (isMobile ? "center" : "flex-start")};
  text-align: ${({ isMobile }) => (isMobile ? "center" : "left")};
  padding: 10px 5px;
  grid-column: ${({ isMobile, numColumns }) =>
    isMobile ? `span ${numColumns}` : "span 1"};
  border-left: none;
  min-width: 140px;
  text-align: center;
  justify-content: center;
`

const LineBox = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
`
const Line = styled.div`
  height: 3px;
  width: 32px;
  background: #cac6c6;
  border-radius: 15px;
`

const getIcon = (
  name: Sensaienum_Componentindividualtablecell_Content,
  isHighlighted: boolean
) => {
  switch (name) {
    case "check":
      return (
        <CheckIcon
          style={{
            fontSize: "2.5rem",
            color: isHighlighted ? "#1d99b8" : "#CAC6C6",
          }}
        />
      )
    case "x":
      return (
        <CloseIcon
          style={{
            fontSize: "2.5rem",
            color: isHighlighted ? "#1d99b8" : "#CAC6C6",
          }}
        />
      )
    case "empty":
      return (
        <LineBox>
          <Line />
        </LineBox>
      )
    case "question":
      return (
        <QuestionIcon
          style={{
            fontSize: "2.5rem",
            color: isHighlighted ? "#1d99b8" : "#CAC6C6",
          }}
        />
      )
    case "textOnly":
      return <></>
  }
}

const Table = (section: SensaiComponentZonesTableZone) => {
  const { table } = section
  if (!table) {
    return null
  }
  const { name, columns, rows, showTitle } = table
  if (!columns || !rows) {
    return null
  }
  const hideRowHeader = rows.every(row => !row?.title)

  let highlightedCol: number | null = null
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  return (
    <PaddedContainer>
      {showTitle && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Typography
            variant="h3"
            color="textSecondary"
            className="gradientText"
            fontSize={"1.75rem"}
            fontWeight={600}
            marginBottom={"1.5rem"}
          >
            {name}
          </Typography>
        </Box>
      )}

      <Grid
        cols={!hideRowHeader ? columns.length + 1 : columns.length}
        rows={rows.length + 1}
        isMobile={isMobile}
        className={isMobile ? "scroll-shadows-horizontal" : ""}
      >
        {!hideRowHeader && <ColumnHeadingBox></ColumnHeadingBox>}
        {columns.map((col, colIdx) => {
          colIdx = colIdx
          const { highlighted, title, image } = col ?? {}
          if (highlighted) highlightedCol = colIdx

          return (
            <ColumnHeadingBox highlighted={highlighted ?? false}>
              {image && (
                <GatsbyImage
                  image={image.imageFile?.childImageSharp?.gatsbyImageData}
                  style={{}}
                  objectFit="contain"
                  objectPosition="50% 50%"
                  alt={title ?? image.alternativeText ?? ""}
                />
              )}
              {!image && <ColumnHeading>{title}</ColumnHeading>}
            </ColumnHeadingBox>
          )
        })}
        {rows.map((row, rowIdx) => {
          const { cells, title } = row ?? { cells: [] }
          return (
            <>
              {!hideRowHeader && (
                <RowHeader isMobile={false} numColumns={columns.length}>
                  {title}
                </RowHeader>
              )}

              {cells?.map((cell, idx) => {
                const isHighlighted =
                  highlightedCol !== null && idx === highlightedCol
                return (
                  <Cell
                    highlighted={isHighlighted}
                    isMobile={isMobile}
                    idx={rowIdx}
                    rows={rows.length}
                    colIdx={idx}
                  >
                    {getIcon(
                      cell?.content ??
                        Sensaienum_Componentindividualtablecell_Content.Empty,
                      isHighlighted
                    )}
                    {cell?.description && (
                      <Box sx={{ color: "#646363", marginTop: "0.4rem" }}>
                        {cell?.description}
                      </Box>
                    )}
                  </Cell>
                )
              })}
            </>
          )
        })}
      </Grid>
    </PaddedContainer>
  )
}

export default Table
